import { ReactNode, useCallback, useRef, useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";

interface SplitViewProps {
  left: ReactNode;
  right: ReactNode;
  /** 오른쪽 패널 초기 너비 (드래그 전). 예: 300, "65%" */
  defaultRightWidth?: number | string;
  minLeft?: number;
  minRight?: number;
  /** 핸들 폭 = 두 패널 사이 간격 */
  handleWidth?: number;
  sx?: SxProps<Theme>;
  leftSx?: SxProps<Theme>;
  rightSx?: SxProps<Theme>;
  onResize?: (rightWidth: number) => void;
}

export function SplitView({
  left,
  right,
  defaultRightWidth = 300,
  minLeft = 200,
  minRight = 240,
  handleWidth = 16,
  sx,
  leftSx,
  rightSx,
  onResize,
}: SplitViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef({ x: 0, width: 0 });
  const [rightWidth, setRightWidth] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!rightRef.current) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, width: rightRef.current.offsetWidth };
    setDragging(true);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging || !containerRef.current) return;
      const container = containerRef.current;
      const style = getComputedStyle(container);
      const inner = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);

      const next = dragStart.current.width - (e.clientX - dragStart.current.x);
      const max = inner - handleWidth - minLeft;
      const clamped = Math.min(Math.max(next, minRight), max);
      setRightWidth(clamped);
      onResize?.(clamped);
    },
    [dragging, handleWidth, minLeft, minRight, onResize],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={[
        {
          display: "flex",
          userSelect: dragging ? "none" : "auto",
          cursor: dragging ? "col-resize" : "auto",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={[{ flex: 1, minWidth: minLeft, height: "100%" }, ...(Array.isArray(leftSx) ? leftSx : [leftSx])]}>
        {left}
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={() => setRightWidth(null)}
        sx={{
          width: handleWidth,
          flexShrink: 0,
          cursor: "col-resize",
          display: "flex",
          justifyContent: "center",
          touchAction: "none",
          "&::after": {
            content: '""',
            width: "2px",
            height: "100%",
            borderRadius: "1px",
            background: dragging ? "var(--dk-border, rgba(0,0,0,0.25))" : "transparent",
            transition: "background 0.15s",
          },
          "&:hover::after": {
            background: "var(--dk-border, rgba(0,0,0,0.2))",
          },
        }}
      />

      <Box
        ref={rightRef}
        sx={[
          { width: rightWidth ?? defaultRightWidth, flexShrink: 0 },
          ...(Array.isArray(rightSx) ? rightSx : [rightSx]),
        ]}
      >
        {right}
      </Box>
    </Box>
  );
}
